import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { sounds } from '@/audio/synth';

export interface CinematicSpec {
  id: string;
  type: 'breaking' | 'welcome-back' | 'ascension' | 'true-death';
  headline: string;
  subhead?: string;
  durationMs?: number;     // auto-dismiss after; 0 = until tap
}

interface CinematicStore {
  current: CinematicSpec | null;
  fire: (spec: CinematicSpec) => void;
  dismiss: () => void;
}

export const useCinematic = create<CinematicStore>()(
  immer((set) => ({
    current: null,
    fire: (spec) => set((s) => { s.current = spec; }),
    dismiss: () => set((s) => { s.current = null; }),
  })),
);

interface TypeStyle {
  chyron: string;          // chyron background color
  chyronText: string;
  chyronLabel: string;
  bgClass: string;         // backdrop class
  bgOverlay?: string;      // additional overlay style
  headlineShadow: string;
  textColor: string;
}

const STYLES: Record<CinematicSpec['type'], TypeStyle> = {
  breaking: {
    chyron: 'bg-[var(--critical)]',
    chyronText: 'text-white',
    chyronLabel: 'BREAKING',
    bgClass: 'bg-black/95 backdrop-blur-sm',
    bgOverlay: 'repeating-linear-gradient(0deg, transparent 0px, transparent 2px, rgba(255, 100, 100, 0.04) 3px, transparent 4px)',
    headlineShadow: '0 0 30px rgba(255, 80, 80, 0.4)',
    textColor: 'text-white',
  },
  'welcome-back': {
    chyron: 'bg-[var(--accent-primary)]',
    chyronText: 'text-black',
    chyronLabel: 'WELCOME BACK',
    bgClass: 'bg-[var(--bg-deep)]/97 backdrop-blur',
    bgOverlay: 'radial-gradient(circle at center, rgba(255, 184, 77, 0.08) 0%, transparent 60%)',
    headlineShadow: '0 0 24px rgba(255, 184, 77, 0.3)',
    textColor: 'text-[var(--text-primary)]',
  },
  ascension: {
    chyron: 'bg-[var(--positive)]',
    chyronText: 'text-black',
    chyronLabel: 'ASCENSION',
    bgClass: 'bg-black/97 backdrop-blur',
    bgOverlay: 'radial-gradient(circle at center, rgba(78, 201, 176, 0.18) 0%, rgba(78, 201, 176, 0.04) 35%, transparent 70%)',
    headlineShadow: '0 0 40px rgba(78, 201, 176, 0.5)',
    textColor: 'text-[var(--positive)]',
  },
  'true-death': {
    chyron: 'bg-white',
    chyronText: 'text-black',
    chyronLabel: 'TRUE DEATH',
    bgClass: 'bg-black backdrop-blur-md',
    bgOverlay: 'repeating-linear-gradient(45deg, transparent 0px, transparent 30px, rgba(255, 255, 255, 0.02) 31px, transparent 32px)',
    headlineShadow: '0 0 8px rgba(255, 255, 255, 0.6)',
    textColor: 'text-white',
  },
};

export function CinematicTakeover() {
  const current = useCinematic((s) => s.current);
  const dismiss = useCinematic((s) => s.dismiss);
  const [revealStage, setRevealStage] = useState(0);

  useEffect(() => {
    if (!current) {
      setRevealStage(0);
      return;
    }
    sounds.notifyAlert();
    const t1 = setTimeout(() => setRevealStage(1), 100);
    const t2 = setTimeout(() => setRevealStage(2), 700);
    const dur = current.durationMs ?? 4000;
    const tDis = dur > 0 ? setTimeout(dismiss, dur) : undefined;
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (tDis) clearTimeout(tDis);
    };
  }, [current, dismiss]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (current && (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter')) {
        e.preventDefault();
        dismiss();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, dismiss]);

  if (!current) return null;
  const style = STYLES[current.type];

  return (
    <div
      onClick={dismiss}
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center cursor-pointer ${style.bgClass}`}
      role="dialog"
      aria-modal="false"
    >
      {style.bgOverlay && (
        <div
          className="absolute inset-0 opacity-100 mix-blend-screen"
          style={{ background: style.bgOverlay }}
        />
      )}
      <div className="relative max-w-3xl px-6 text-center">
        {revealStage >= 1 && (
          <div className={`mb-3 inline-block px-3 py-1 font-mono text-mono-sm font-bold uppercase tracking-widest ${style.chyron} ${style.chyronText}`}>
            {style.chyronLabel}
          </div>
        )}
        {revealStage >= 1 && (
          <h2
            className={`font-news text-3xl font-bold leading-tight sm:text-5xl ${style.textColor}`}
            style={{ textShadow: style.headlineShadow }}
          >
            {current.headline}
          </h2>
        )}
        {revealStage >= 2 && current.subhead && (
          <p className="mt-4 font-mono text-mono-sm text-[var(--text-secondary)]">
            {current.subhead}
          </p>
        )}
        {revealStage >= 2 && (
          <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
            tap / esc to dismiss
          </p>
        )}
      </div>
    </div>
  );
}
